'use client'

import { useRouter } from 'next/navigation'
import { BookOpen, MapPin, X } from 'lucide-react'
import React from 'react'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useLocale } from '@/lib/i18n/useLocale'
import type { CityId } from '@/lib/mock/mapLocations'
import { getLocationHref, type LocationCampusOption } from '@/lib/mock/locationPages'
import { cn } from '@/utilities/ui'

type LocationCampusDropdownVariant = 'location' | 'map'
type LocationCampusDropdownIcon = 'book' | 'mapPin'

const EMPTY_VALUE = '__location-campus-empty__'

type LocationCampusDropdownProps = {
  /** When true and an option is selected, trigger text/icons use turquoise accent (map variant). */
  accentSelected?: boolean
  campuses: LocationCampusOption[]
  citySlug?: CityId
  currentSlug: string
  /** Optional formatter for the selected trigger label (e.g. `Distance to ${name}`). */
  formatSelectedLabel?: (name: string) => string
  fullWidth?: boolean
  /** When true, the trigger keeps showing `label` after selection (full name stays in aria-label). */
  keepTriggerLabel?: boolean
  /** When set, selected names longer than this are shortened in the trigger (full name in aria-label/title). */
  selectedLabelMaxLength?: number
  clearLabel?: string
  icon?: LocationCampusDropdownIcon
  /** Map control that shows only the leading icon (used on narrow city-map rows). */
  iconOnly?: boolean
  label: string
  listAriaLabel?: string
  onClear?: () => void
  onSelect?: (campus: LocationCampusOption) => void
  uppercase?: boolean
  variant?: LocationCampusDropdownVariant
  /** When true, menu options wrap within a viewport-safe max width. */
  wrapMenuOptions?: boolean
}

const mapTriggerClassName =
  'inline-flex h-9 items-center gap-2 rounded-full border border-black/10 bg-transparent px-3 font-outfit text-sm font-semibold uppercase tracking-wide text-dark-800/90'

const triggerClassNames: Record<LocationCampusDropdownVariant, string> = {
  location:
    'inline-flex h-11 items-center gap-2 rounded-[10px] border-[1.5px] border-turquoise bg-transparent p-3 font-outfit text-base font-medium text-turquoise transition-colors hover:bg-turquoise/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent data-placeholder:text-turquoise',
  map: cn(
    mapTriggerClassName,
    'transition-colors hover:bg-cream focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50 focus-visible:ring-offset-2 focus-visible:ring-offset-cream',
  ),
}

export { mapTriggerClassName as locationMapHeroTriggerClassName }

const contentClassNames: Record<LocationCampusDropdownVariant, string> = {
  location:
    'z-[60] max-h-60 min-w-[16rem] overscroll-contain overflow-y-auto rounded-[10px] border border-white/20 bg-bluetext/95 py-1 shadow-[0px_16px_32px_-8px_rgba(12,12,13,0.4)] backdrop-blur-md ring-0',
  map: 'z-[60] max-h-60 min-w-[16rem] overscroll-contain overflow-y-auto rounded-[10px] border border-black/10 bg-white py-1 shadow-[0px_16px_32px_-8px_rgba(0,33,64,0.12)] ring-0',
}

const optionClassNames: Record<LocationCampusDropdownVariant, string> = {
  location:
    'rounded-none px-4 py-2.5 pr-4 font-outfit text-sm font-medium text-white focus:bg-white/10 focus:!text-white focus:**:!text-white data-highlighted:bg-white/10 data-highlighted:!text-white data-highlighted:**:!text-white data-[state=checked]:bg-white/10 data-[state=checked]:!text-turquoise data-[state=checked]:**:!text-turquoise [&>span:first-child]:hidden',
  map: 'rounded-none px-4 py-2.5 pr-4 font-outfit text-sm font-medium !text-dark-800 focus:!bg-cream focus:!text-dark-800 focus:**:!text-dark-800 data-highlighted:!bg-cream data-highlighted:!text-dark-800 data-highlighted:**:!text-dark-800 data-[state=checked]:!bg-cream data-[state=checked]:!text-bluetext data-[state=checked]:**:!text-bluetext [&>span:first-child]:hidden',
}

const wrappedMapOptionClassName =
  'whitespace-normal break-words text-left leading-snug [&_[data-slot=select-item-text]]:whitespace-normal [&_[data-slot=select-item-text]]:break-words'

function truncateSelectedLabel(text: string, maxLength: number): string {
  if (text.length <= maxLength) {
    return text
  }

  const slice = text.slice(0, maxLength).trimEnd()
  const lastSpace = slice.lastIndexOf(' ')

  if (lastSpace > maxLength * 0.55) {
    return `${slice.slice(0, lastSpace)}…`
  }

  return `${slice}…`
}

function CampusTriggerIcon({
  icon,
  variant,
}: {
  icon: LocationCampusDropdownIcon
  variant: LocationCampusDropdownVariant
}) {
  const iconClassName =
    variant === 'map' ? 'size-4 shrink-0 text-dark-green' : 'size-4 shrink-0 text-turquoise'

  if (icon === 'mapPin') {
    return <MapPin aria-hidden className={iconClassName} />
  }

  return <BookOpen aria-hidden className={iconClassName} />
}

export function LocationCampusDropdown({
  accentSelected = false,
  campuses,
  citySlug,
  clearLabel,
  currentSlug,
  formatSelectedLabel,
  fullWidth = false,
  icon = 'book',
  iconOnly = false,
  keepTriggerLabel = false,
  label,
  listAriaLabel = 'Available campuses',
  onClear,
  onSelect,
  selectedLabelMaxLength,
  uppercase = false,
  variant = 'location',
  wrapMenuOptions = false,
}: LocationCampusDropdownProps) {
  const soleOption = campuses.length === 1 ? campuses[0] : undefined
  const selectedOption =
    campuses.find((campus) => campus.slug === currentSlug) ?? soleOption
  const hasSelection = Boolean(selectedOption)
  const useAccent = accentSelected && hasSelection && variant === 'map'
  const isStatic = campuses.length <= 1

  const triggerClassName = cn(
    triggerClassNames[variant],
    'shadow-none ring-0',
    variant === 'location' &&
      '!h-11 min-h-11 !bg-transparent !p-3 dark:!bg-transparent hover:!bg-turquoise/10 dark:hover:!bg-turquoise/10',
    variant === 'map' &&
      '!h-9 min-h-9 !bg-transparent !px-3 !py-0 hover:!bg-cream dark:!bg-transparent dark:hover:!bg-cream',
    iconOnly &&
      '!size-9 w-9 min-w-9 shrink-0 justify-center !px-0 [&>svg:last-child]:hidden',
    fullWidth && !iconOnly && 'w-full min-w-0 max-w-full overflow-hidden',
    uppercase && '[&_[data-slot=select-value]]:uppercase',
    variant === 'location' && '[&_[data-slot=select-value]]:text-turquoise [&_[data-slot=select-value]]:text-base',
    variant === 'map' &&
      (useAccent
        ? 'text-dark-green [&_[data-slot=select-value]]:text-dark-green'
        : '[&_[data-slot=select-value]]:text-dark-800/90'),
    variant === 'location' && '[&>svg:first-child]:text-turquoise',
    variant === 'map' && '[&>svg:first-child]:text-dark-green',
    !isStatic && '[&>svg:last-child]:size-4 [&>svg:last-child]:shrink-0',
    !isStatic && variant === 'location' && '[&>svg:last-child]:text-turquoise',
    !isStatic &&
      variant === 'map' &&
      (useAccent
        ? '[&>svg:last-child]:text-dark-green'
        : '[&>svg:last-child]:opacity-60 [&>svg:last-child]:text-dark-800'),
    selectedOption &&
      selectedLabelMaxLength != null &&
      !fullWidth &&
      variant === 'map' &&
      '[&_[data-slot=select-value]]:max-w-[11rem] [&_[data-slot=select-value]]:truncate',
  )
  const contentClassName = cn(
    contentClassNames[variant],
    wrapMenuOptions && 'max-w-[min(16rem,calc(100vw-2rem))]',
  )
  const optionClassName = cn(
    optionClassNames[variant],
    wrapMenuOptions && variant === 'map' && wrappedMapOptionClassName,
  )
  const locale = useLocale()
  const router = useRouter()

  const selectValue =
    currentSlug && campuses.some((campus) => campus.slug === currentSlug)
      ? currentSlug
      : EMPTY_VALUE
  const rawSelectedLabel = selectedOption
    ? (formatSelectedLabel?.(selectedOption.name) ?? selectedOption.name)
    : label
  const compactSelectedLabel =
    selectedOption && selectedLabelMaxLength != null
      ? truncateSelectedLabel(rawSelectedLabel, selectedLabelMaxLength)
      : rawSelectedLabel
  const triggerLabel =
    selectedOption && !keepTriggerLabel ? compactSelectedLabel : label
  const displayLabel = uppercase ? triggerLabel.toUpperCase() : triggerLabel
  const fullSelectedLabel = selectedOption ? rawSelectedLabel : undefined
  const accessibleLabel = selectedOption ? `${label}: ${selectedOption.name}` : label
  const canClear = Boolean(selectedOption && onClear)

  const handleValueChange = (value: string) => {
    if (value === EMPTY_VALUE) {
      return
    }

    const campus = campuses.find((option) => option.slug === value)

    if (!campus) {
      return
    }

    if (onSelect) {
      onSelect(campus)
      return
    }

    if (citySlug) {
      router.push(getLocationHref(citySlug, campus.slug, locale))
    }
  }

  const wrapperClassName = cn(
    iconOnly && 'shrink-0',
    fullWidth && !iconOnly && 'min-w-0 flex-1',
  )
  const pillClassName = cn(
    triggerClassName,
    'flex-nowrap',
    fullWidth && !iconOnly && 'flex w-full min-w-0',
  )
  const innerTriggerClassName = cn(
    'h-full min-h-0 !w-full min-w-0 flex-1 justify-between gap-2 border-0 bg-transparent p-0 shadow-none ring-0 hover:!bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 dark:hover:!bg-transparent',
    uppercase && '[&_[data-slot=select-value]]:uppercase',
    variant === 'location' &&
      '[&_[data-slot=select-value]]:text-turquoise [&_[data-slot=select-value]]:text-base',
    variant === 'map' &&
      (useAccent
        ? 'text-dark-green [&_[data-slot=select-value]]:text-dark-green'
        : '[&_[data-slot=select-value]]:text-dark-800/90'),
    variant === 'location' && '[&>svg:first-child]:text-turquoise',
    variant === 'map' && '[&>svg:first-child]:text-dark-green',
    !canClear && '[&>svg:last-child]:size-4 [&>svg:last-child]:shrink-0',
    !canClear && variant === 'location' && '[&>svg:last-child]:text-turquoise',
    !canClear &&
      variant === 'map' &&
      (useAccent
        ? '[&>svg:last-child]:text-dark-green'
        : '[&>svg:last-child]:opacity-60 [&>svg:last-child]:text-dark-800'),
    selectedOption &&
      selectedLabelMaxLength != null &&
      !fullWidth &&
      variant === 'map' &&
      '[&_[data-slot=select-value]]:max-w-[11rem] [&_[data-slot=select-value]]:truncate',
  )

  if (isStatic) {
    return (
      <div className={wrapperClassName}>
        <span
          className={cn(
            triggerClassName,
            'cursor-default hover:!bg-transparent',
            fullWidth && !iconOnly && 'flex w-full min-w-0',
          )}
        >
          <CampusTriggerIcon icon={icon} variant={variant} />
          {iconOnly ? (
            <span className="sr-only">{displayLabel}</span>
          ) : (
            <span className="min-w-0 truncate" title={fullSelectedLabel ?? displayLabel}>
              {displayLabel}
            </span>
          )}
        </span>
      </div>
    )
  }

  const handleClear = (event: React.MouseEvent | React.PointerEvent) => {
    event.preventDefault()
    event.stopPropagation()
    onClear?.()
  }

  return (
    <div className={wrapperClassName}>
      <div
        className={cn(
          pillClassName,
          'focus-within:outline-none focus-within:ring-2 focus-within:ring-turquoise/50',
          variant === 'map' && 'focus-within:ring-offset-2 focus-within:ring-offset-cream',
        )}
      >
        <div className="min-w-0 flex-1">
          <Select onValueChange={handleValueChange} value={selectValue}>
            <SelectTrigger
              aria-label={accessibleLabel}
              className={innerTriggerClassName}
              hideIcon={canClear}
              size="default"
              title={fullSelectedLabel}
            >
              <CampusTriggerIcon icon={icon} variant={variant} />
              {iconOnly ? (
                <span className="sr-only">{displayLabel}</span>
              ) : selectedOption && !keepTriggerLabel ? (
                <SelectValue>
                  {uppercase ? compactSelectedLabel.toUpperCase() : compactSelectedLabel}
                </SelectValue>
              ) : (
                <span
                  className={cn(
                    'truncate font-outfit',
                    fullWidth && 'min-w-0 flex-1 text-left',
                    variant === 'location' && 'text-base font-medium text-turquoise',
                    variant === 'map' &&
                      'text-sm font-semibold uppercase tracking-wide text-dark-800/90',
                  )}
                >
                  {displayLabel}
                </span>
              )}
            </SelectTrigger>

            <SelectContent
              align="start"
              aria-label={listAriaLabel}
              className={contentClassName}
              hideScrollButtons
              onCloseAutoFocus={(event) => event.preventDefault()}
              onWheel={(event) => event.stopPropagation()}
              position="popper"
              sideOffset={8}
              viewportClassName="max-h-60 overflow-y-auto overscroll-contain data-[position=popper]:!h-auto data-[position=popper]:max-h-60"
            >
              {campuses.map((campus) => (
                <SelectItem key={campus.slug} className={optionClassName} value={campus.slug}>
                  {campus.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {canClear ? (
          <button
            aria-label={clearLabel}
            className="inline-flex size-7 shrink-0 items-center justify-center rounded-full text-dark-800/70 transition-colors hover:bg-cream hover:text-dark-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50"
            onClick={handleClear}
            onPointerDown={handleClear}
            type="button"
          >
            <X aria-hidden className="size-3.5" />
          </button>
        ) : null}
      </div>
    </div>
  )
}
